import { useCallback, useEffect, useMemo, useState } from 'react';

import { ChatBoxHookProps } from './types';
import { useAIResponseStream } from './useAIResponseStream';
import { useChatBoxState } from './useChatBoxState';
import { isExpiredTerminalDeveloperProgressMessage, useChatEffects } from './useChatEffects';
import { useChatRefs } from './useChatRefs';
import { useMessageSender } from './useMessageSender';
import { useTicker } from './useTicker';

type ActiveChatSelection = { projectId: string | null; sessionId: string | null };

export const resolveActiveChatSessionId = (
  projectId: string | null,
  routeSessionId: string | null | undefined,
  selection: ActiveChatSelection,
): string | null => {
  // A supplied route value is authoritative, including an explicit null after
  // the active session is deleted. `undefined` retains the legacy event-owned
  // fallback for callers that do not provide route session state.
  if (routeSessionId !== undefined) return routeSessionId;

  return selection.projectId === projectId ? selection.sessionId : null;
};

function useActiveChatSession(projectId: string | null, routeSessionId?: string | null) {
  const [selection, setSelection] = useState<ActiveChatSelection>({
    projectId,
    sessionId: routeSessionId ?? null,
  });

  useEffect(() => {
    if (!projectId) {
      setSelection({ projectId: null, sessionId: null });

      return;
    }

    if (!routeSessionId) return;
    setSelection({ projectId, sessionId: routeSessionId });
  }, [projectId, routeSessionId]);

  useEffect(() => {
    const onSelected = (event: Event) => {
      const detail = (event as globalThis.CustomEvent).detail as {
        projectId?: string | null;
        sessionId?: string | null;
      } | undefined;

      if (detail?.projectId && detail.projectId !== projectId) return;
      setSelection({
        projectId: detail?.projectId ?? projectId,
        sessionId: detail?.sessionId ?? null,
      });
    };

    window.addEventListener('chat_session_selected', onSelected);

    return () => window.removeEventListener('chat_session_selected', onSelected);
  }, [projectId]);

  return resolveActiveChatSessionId(projectId, routeSessionId, selection);
}

export const useChatBox = ({
  projectId,
  sessionId,
  initialProjectBootstrap,
  isSessionResolving = false,
  sessionLoadFailed = false,
}: ChatBoxHookProps) => {
  const { chatState, streamingState, userDisplayName, chatSelectors, streamingSelectors } = useChatBoxState();
  const activeSessionId = useActiveChatSession(projectId, sessionId);
  const isSessionBlocked = isSessionResolving || sessionLoadFailed;
  const projectSelectionMatches = (
    chatSelectors.activeProjectId === projectId
    && chatSelectors.activeSessionId === activeSessionId
  );
  const isProjectBootstrapping = Boolean(projectId) && (
    !projectSelectionMatches
    || (!chatSelectors.hasProjectSnapshot && (
      chatSelectors.projectLoadStatus === 'idle'
      || chatSelectors.projectLoadStatus === 'pending'
    ))
  );
  const isProjectUnavailable = Boolean(projectId) && (
    !projectSelectionMatches || !chatSelectors.hasProjectSnapshot
  );
  const visibleMessages = useMemo(
    () => {
      if (isSessionBlocked) return [];

      const nowMs = Date.now();
      const projectMessages = projectId
        ? chatSelectors.messages.filter((message) => message.project_id === projectId)
        : chatSelectors.messages;
      const sessionMessages = activeSessionId
        ? projectMessages.filter((message) => message.session_id === activeSessionId)
        : projectMessages;
      return sessionMessages.filter((message) => !isExpiredTerminalDeveloperProgressMessage(message, nowMs));
    },
    [activeSessionId, chatSelectors.messages, isSessionBlocked, projectId],
  );
  const effectsProjectId = isSessionBlocked ? null : projectId;

  const {
    refsFull,
    inputRef,
    messagesEndRef,
    chatContainerRef,
    shouldAutoScrollRef,
    getOrderedTimestamp
  } = useChatRefs(visibleMessages);

  useEffect(() => {
    refsFull.setHasTriggeredInitialAI(false);
    refsFull.setShouldAutoScroll(true);
  }, [activeSessionId, projectId, refsFull]);

  const ticker = useTicker(refsFull, streamingState, chatState);

  const triggerInitialAIStream = useAIResponseStream({
    projectId,
    activeSessionId,
    initialProjectBootstrap: Boolean(initialProjectBootstrap),
    refsFull,
    ticker,
    chatState,
    streamingState,
    getOrderedTimestamp,
  });

  const sendMessage = useMessageSender({
    projectId, activeSessionId, refsFull, ticker, chatState, streamingState, getOrderedTimestamp
  });

  useChatEffects(
    effectsProjectId,
    activeSessionId,
    visibleMessages,
    chatState,
    streamingState,
    refsFull,
    triggerInitialAIStream,
  );

  const onScroll = useCallback(() => {
    if (!chatContainerRef.current) return;
    const { scrollTop } = chatContainerRef.current;
    const isBottom = Math.abs(scrollTop) < 50;

    shouldAutoScrollRef.current = isBottom;
  }, [chatContainerRef, shouldAutoScrollRef]);

  return {
    activeSessionId,
    messages: visibleMessages,
    userDisplayName,
    isLoading: chatSelectors.isLoading,
    error: chatSelectors.error,
    projectLoadStatus: chatSelectors.projectLoadStatus,
    projectLoadError: projectSelectionMatches ? chatSelectors.projectLoadError : null,
    isProjectBootstrapping,
    isProjectUnavailable,
    isAiTyping: streamingSelectors.isAiTyping,
    streamingDisplay: streamingSelectors.streamingDisplay,
    currentStreamingMsgId: streamingSelectors.currentStreamingMsgId,

    inputRef,
    chatContainerRef,
    messagesEndRef,

    onInputChange: chatState.setInputValue,
    onSend: sendMessage,
    onScroll,

    triggerInitialAIStream
  };
};
